"use client"
import { Content, isFilled } from "@prismicio/client"
import { SliceComponentProps } from "@prismicio/react"
import { PrismicNextLink } from "@prismicio/next"
import { PrismicNextImage } from "@prismicio/next"
import { PrismicRichText } from "@airplan/ui/prismic-richtext"
import Button from "@aura-design/system/button"
import { useState } from "react"
import { Cross1Icon, EnterFullScreenIcon } from "@radix-ui/react-icons"

const isProduction = process.env.NODE_ENV === "production"

/**
 * Props for `SectionFidis`.
 */
export type SectionFidisProps = SliceComponentProps<Content.SectionFidisSlice>

/**
 * Component for "SectionFidis" Slices.
 */
const SectionFidis = ({ slice, context }: SectionFidisProps): JSX.Element => {
  const { PROJECT_UID } = context
  const [isFullScreen, setIsFullScreen] = useState(false)

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <div className="smesh pad text-dark-blue">
        <div className="nav-list justify-start h-auto">
          {isFilled.image(slice.primary.icon) && (
            <div className="valign mb-2 mr-2">
              <PrismicNextImage
                field={slice.primary.icon}
                height={80}
                width={80}
                alt=''
              />
            </div>
          )}
          {isFilled.richText(slice.primary.title) && (
            <div className="valign badgeline border-t-yellow w-full">
              <PrismicRichText field={slice.primary.title} />
            </div>
          )}
        </div>
      </div>
      <div
        className={
          isFullScreen
            ? "fids-opened fixed top-0 left-0 right-0 bottom-0 z-40 bg-white"
            : "relative"
        }
      >
        {!isFullScreen && (
          <div className="absolute right-0 left-0 top-[-39px] md:top-0 smesh text-right pointer-events-none">
            <Button
              mode="link"
              onClick={() => setIsFullScreen(true)}
              className="pointer-events-auto"
            >
              <EnterFullScreenIcon />
            </Button>
          </div>
        )}
        {isFullScreen && (
          <div className="fixed top-1 right-1">
            <Button mode="link" onClick={() => setIsFullScreen(false)}>
              <Cross1Icon />
            </Button>
          </div>
        )}
        {isFilled.number(slice.primary.limit) ? (
          <iframe
            src={
              isProduction
                ? `https://fids.airplan.aero?airport=${PROJECT_UID}&limit=${slice.primary.limit}`
                : `http://localhost:4004/?airport=${PROJECT_UID}&limit=${slice.primary.limit}`
            }
            className={`w-full h-[70svh]`}
            frameBorder={0}
          />
        ) : (
          <iframe
            src={
              isProduction
                ? `https://fids.airplan.aero?airport=${PROJECT_UID}`
                : `http://localhost:4004/?airport=${PROJECT_UID}`
            }
            className={`w-full h-[200svh]`}
            frameBorder={0}
          />
        )}
      </div>
      {isFilled.keyText(slice.primary.button_label) &&
        isFilled.link(slice.primary.button_link) && (
          <div className="text-center aura">
            <PrismicNextLink
              field={slice.primary.button_link}
              legacyBehavior
              passHref
            >
              <Button label={slice.primary.button_label} />
            </PrismicNextLink>
          </div>
        )}
    </section>
  )
}

export default SectionFidis
